import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { MATERIAL_IMPORTS } from '../../../material/material-imports';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';

import { LessonModel, LessonRequest } from '@generativ/much-smarter-models';
import { Observable } from 'rxjs';

import { LessonService } from '../lesson.service';
import { AlertService } from '../../../core/alert/alert.service';
import { ParamsService } from '../../../shared/params.service';
import { SortListService } from '../../../shared/sort-list.service';
import { FormService } from '../../../shared/form.service';

@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, ...MATERIAL_IMPORTS, CapitalizePipe, SpinnerComponent],
  selector: 'app-lesson',
  templateUrl: './lesson.component.html',
  styleUrls: ['./lesson.component.scss']
})
export class LessonComponent implements OnInit {

  lesson: LessonModel;
  lessonId: number;
  courseId: number;
  exerciseIds: number[] = [];
  lessonForm: FormGroup;
  pageType: string;
  readonly ready = signal(false);
  errorMessage: string;
  isSubmitting = false;

  constructor(private route: ActivatedRoute,
              private formService: FormService,
              private router: Router,
              private lessonService: LessonService,
              private alertService: AlertService,
              private location: Location,
              private sortListService: SortListService,
              private paramsService: ParamsService) {
    this.lessonForm = this.formService.createForm(this.formOptions);
  }


  ngOnInit() {
    this.courseId = +this.paramsService.getParamById('courseId', this.route.snapshot);
    this.route.data.subscribe((data) => {
      this.pageType = data.type;
      if (this.pageType === 'edit') {
        this.route.params.subscribe((params: Params) => {
          this.lessonId = +params['lessonId'];
          this.lessonService.getLesson(this.lessonId).subscribe(
            lessonModel => {
              this.lesson = lessonModel;
              this.setFormValue(lessonModel);
              this.ready.set(true);
            },
            err => {
              this.alertService.error(err);
              this.ready.set(true);
            }
          );
        });
      } else {
        this.ready.set(true);
      }
    });
  }

  submitForm() {
    if (this.formService.validateFormAndUpdateErrors(this.lessonForm)) {
      let lessonObservable: Observable<LessonModel>;
      let message;
      this.errorMessage = null;

      if (this.isSubmitting) {
        return;
      }

      if (!this.lessonForm.valid) {
        this.errorMessage = 'Errors on form. See fields below.';
        return;
      }

      this.isSubmitting = true;

      if (this.pageType === 'create') {
        const lesson = this.parseToModel(new LessonRequest.Create());
        lesson.courseId = this.courseId;
        message = 'Lesson created';
        lessonObservable = this.lessonService.createLesson(lesson);
      } else {
        const lesson = <LessonRequest.Update> this.parseToModel(new LessonRequest.Update());
        lesson.parse(this.lessonForm.value);
        lesson.exerciseIds = this.exerciseIds;
        lesson.courseId = this.courseId;
        message = 'Lesson updated';
        lessonObservable = this.lessonService.updateLesson(this.lessonId, lesson);
      }

      lessonObservable.subscribe(
        lessonModel => {
          this.alertService.success(message);
          if (this.pageType === 'create') {
            this.router.navigate([`../${lessonModel.id}/exercises/create`], {relativeTo: this.route});
          } else {
            this.location.back();
          }
        },
        err => {
          this.isSubmitting = false;
          this.alertService.error(err);
        }
      );
    }
  }

  /**
   * Drag end: Update lesson ids with the new array of order.
   */
  dragEnd() {
    this.exerciseIds = this.sortListService.dragEnd(this.lesson.exercises.map(exercise => exercise.id));
  }

  get buttonName(): string {
    return this.pageType === 'edit' ? 'SAVE' : this.pageType.toUpperCase();
  }

  private parseToModel(lessonModel: LessonRequest.Create | LessonRequest.Update): (LessonRequest.Create | LessonRequest.Update) {
    lessonModel.parse(this.lessonForm.value);
    lessonModel.strategyLessonEnabled = this.lessonForm.get('strategyLessonEnabled').value ? 1: 0;
    lessonModel.smartSaversEnabled = this.lessonForm.get('smartSaversEnabled').value ? 1: 0;
    lessonModel.showCorrectEnabled = this.lessonForm.get('showCorrectEnabled').value ? 1: 0;
    lessonModel.showMetricsEnabled = this.lessonForm.get('showMetricsEnabled').value ? 1: 0;
    lessonModel.openAnswerOnComplete = this.lessonForm.get('openAnswerOnComplete').value ? 1: 0;


    return lessonModel;
  }

  private setFormValue(lessonModel: LessonModel) {
    this.lessonForm.patchValue(lessonModel);
    this.lessonForm.get('strategyLessonEnabled').setValue(!!lessonModel.strategyLessonEnabled);
    this.lessonForm.get('smartSaversEnabled').setValue(!!lessonModel.smartSaversEnabled);
    this.lessonForm.get('showCorrectEnabled').setValue(!!lessonModel.showCorrectEnabled);
    this.lessonForm.get('showMetricsEnabled').setValue(!!lessonModel.showMetricsEnabled);
    this.lessonForm.get('openAnswerOnComplete').setValue(!!lessonModel.openAnswerOnComplete);
  }

  private  get formOptions(): object {
    return {
      'title': [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(100)
        ])
      ],
      'description': [
        null,
        Validators.compose([
          Validators.maxLength(1000)
        ])],
      'strategyLessonEnabled': [
        false,
        Validators.compose([
          Validators.required
        ])],
      'smartSaversEnabled': [
        false,
        Validators.compose([
          Validators.required
        ])],
      'showCorrectEnabled': [
        false,
        Validators.compose([
          Validators.required
        ])],
      'showMetricsEnabled': [
        false,
        Validators.compose([
          Validators.required
        ])],
      'openAnswerOnComplete': [
        false,
        Validators.compose([
          Validators.required
        ])],
    };
  }
}
